import {Alert, Badge, Code, Group, Loader, Stack, Table, Text} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';
import type {NameAvailability, NameAvailabilityResult} from '../../devops/name-availability';

type DerivedNamesPanelProps = {
    result?: NameAvailabilityResult,
    isFetching: boolean,
};

// FNP-1500: EVERY DERIVED NAME, VISIBLE BEFORE ANYTHING IS BUILT.
//
// ═══ THE FAILURE THIS PREVENTS ═══
//
// Provisioning derives two repository names, two pipeline names, a stack name
// per stage and a deployment bucket from ONE base name, and checked none of
// them. A collision first surfaced at CreateInfraRepo — step 7 of 11 — after a
// repository had already been created, leaving a FAILED record and a PENDING
// inventory row that nothing cleans up.
//
// The owner hit exactly this on 2026-09-13. The refusal for `flexi-testing-app`
// suggested `flexi-testing`, which derives `flexi-testing-app-infra` — a repo
// that already existed. The error message recommended a name that would have
// failed differently, later, and messier.
//
// ⚠️ UNKNOWN IS NOT FREE. A listing that fails permissions returns zero items
// and is indistinguishable from an empty account, so a lookup that could not
// complete is shown as unknown and blocks Provision exactly as a collision
// does. Rendering it as free would be the same silent-success class the
// teardown's `verify-infrastructure-absent` was written to avoid.
const statusBadge = (status: NameAvailability['status']) => {
    if (status === 'FREE') {
        return <Badge color={'green'} variant={'light'}>Free</Badge>;
    }
    if (status === 'TAKEN') {
        return <Badge color={'red'} variant={'light'}>Taken</Badge>;
    }
    return <Badge color={'orange'} variant={'light'}>Could not check</Badge>;
};

export const DerivedNamesPanel = ({
                                      result,
                                      isFetching,
                                  }: DerivedNamesPanelProps) => {

    if (!result) {
        return null;
    }

    // A base name the rules already reject gets the reason and nothing else.
    // Showing per-name statuses here would be a fiction — no lookup ran.
    if (result.violations.length > 0) {
        return (
            <Alert variant={'light'} color={'red'} icon={<AiOutlineWarning/>}>
                <Stack gap={'xs'}>
                    {result.violations.map((violation) => (
                        <Text key={violation.rule} size={'sm'}>{violation.message}</Text>
                    ))}
                </Stack>
            </Alert>
        );
    }

    return (
        <Stack gap={'sm'}>
            <Group gap={'xs'}>
                <Text size={'sm'} fw={500}>This will create</Text>
                {isFetching ? <Loader size={'xs'}/> : null}
            </Group>

            <Table withTableBorder verticalSpacing={'xs'}>
                <Table.Tbody>
                    {result.names.map((entry) => (
                        <Table.Tr key={entry.kind}>
                            <Table.Td w={180}>
                                <Text size={'xs'} c={'dimmed'}>
                                    {entry.resourceType}
                                    {entry.stage ? ` (${entry.stage})` : ''}
                                </Text>
                            </Table.Td>
                            <Table.Td>
                                {/*
                                  * ⚠️ READ-ONLY, DELIBERATELY (FNP-1561).
                                  *
                                  * These boxes used to be editable, on the
                                  * reasoning that an operator hitting one
                                  * collision should rename that one resource
                                  * rather than everything. The edit never went
                                  * anywhere: the drawer held it in state, the
                                  * panel echoed it back, and `EditPage`'s
                                  * onConfirm destructured it away — the same
                                  * statement, and the same mistake, that
                                  * FNP-1544 fixed for `baseName`.
                                  *
                                  * Nor could it have worked. The availability
                                  * check runs off the base name and the
                                  * playbook's suffix (FNP-1557), never off an
                                  * override, so an edited name was not even
                                  * the name being checked — and
                                  * `ProvisionApplicationInput` has no field to
                                  * carry one.
                                  *
                                  * 🛑 DO NOT MAKE THESE EDITABLE AGAIN without
                                  * a field on the input and a single
                                  * application site. The orchestrator derives
                                  * these names in eight places; honouring an
                                  * override at some and not others is what
                                  * FNP-1544's one-site normalisation exists to
                                  * prevent. The base name already renames all
                                  * four together and does work.
                                  */}
                                <Code data-testid={`derived-name-${entry.kind}`}>
                                    {entry.name}
                                </Code>
                            </Table.Td>
                            <Table.Td w={140}>
                                {statusBadge(entry.status)}
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>

            {result.taken.length > 0 && (
                <Alert variant={'light'} color={'red'} icon={<AiOutlineWarning/>}>
                    <Text size={'sm'}>
                        {result.taken.length === 1 ? 'One name is' : `${result.taken.length} names are`}
                        {' '}already taken. Choose a different base name.
                    </Text>
                </Alert>
            )}
        </Stack>
    );
};
